/**
 * Session Utilities
 * 
 * Helper functions for parsing and formatting session information
 */

/**
 * Parse device information from User-Agent string
 * 
 * @param userAgent - User-Agent header string (optional, defaults to navigator.userAgent)
 * @returns Device type string (e.g., "Chrome on Mac", "Safari on iPhone")
 */
export function parseDeviceInfo(userAgent?: string): string {
  const ua = userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (!ua) {
    return 'Unknown Device';
  }

  // Browser detection
  let browser = 'Unknown Browser';
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browser = 'Chrome';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
  } else if (ua.includes('Edg')) {
    browser = 'Edge';
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    browser = 'Opera';
  }

  // OS detection
  let os = 'Unknown OS';
  if (ua.includes('Mac OS X')) {
    os = 'Mac';
  } else if (ua.includes('Windows')) {
    os = 'Windows';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = ua.includes('iPad') ? 'iPad' : 'iPhone';
  }

  return `${browser} on ${os}`;
}

/**
 * Parse device information object from User-Agent
 * Returns detailed device info as JSONB object
 */
export function parseDeviceInfoObject(userAgent?: string): { browser: string; os: string; platform: string; deviceType: string } {
  const ua = userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  
  // Browser detection
  let browser = 'Unknown Browser';
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browser = 'Chrome';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
  } else if (ua.includes('Edg')) {
    browser = 'Edge';
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    browser = 'Opera';
  }

  // OS detection
  let os = 'Unknown OS';
  if (ua.includes('Mac OS X')) {
    os = 'Mac';
  } else if (ua.includes('Windows')) {
    os = 'Windows';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = ua.includes('iPad') ? 'iPad' : 'iPhone';
  }

  // Platform detection
  const platform = typeof navigator !== 'undefined' ? navigator.platform : 'Unknown';
  const deviceType = parseDeviceInfo(ua);

  return {
    browser,
    os,
    platform,
    deviceType
  };
}

/**
 * Format session location string
 * 
 * @param city - City name (optional)
 * @param country - Country name (optional)
 * @returns Formatted location string (e.g., "Sydney, Australia" or "Unknown Location")
 */
export function formatSessionLocation(city: string | null, country: string | null): string {
  if (!city && !country) {
    return 'Unknown Location';
  }

  if (city && country) {
    return `${city}, ${country}`;
  }

  return city || country || 'Unknown Location';
}

/**
 * Check if a session token matches the current session
 * 
 * @param sessionToken - Session token to check
 * @param currentToken - Current session token
 * @returns True if tokens match (is current session)
 */
export function isCurrentSession(sessionToken: string, currentToken: string): boolean {
  return sessionToken === currentToken;
}

